import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Tenant as DatabaseTenant, TenantSettings } from "@sylva/shared-types";
import { useAuth } from "./useAuth";

// Extend database Tenant type with UI-specific fields
export interface Tenant extends DatabaseTenant {
  userRole?: "owner" | "admin" | "manager" | "member" | "viewer" | "staff";
}

// Re-export for convenience
export type { TenantSettings };

export const useTenants = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCheckedTenants, setHasCheckedTenants] = useState(false);
  // Schlaegt das Laden fehl, blieb `tenants` bisher einfach leer -- und die
  // Oberflaeche meldete "kein Salon zugeordnet". Bei einer Stoerung ist das
  // schlicht falsch, deshalb halten wir den Fehler fest.
  const [loadError, setLoadError] = useState<Error | null>(null);

  const fetchTenants = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch tenants through user_tenants table for proper membership management
      const { data, error } = await supabase
        .from("user_tenants")
        .select(`
          tenant_id,
          role,
          is_active,
          tenants (*)
        `)
        .eq("user_id", user?.id || '')
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform the data to match our Tenant interface.
      // A deactivated tenant is out of service and must not be selectable, so
      // it is dropped here rather than hidden later in the UI.
      const transformedTenants: Tenant[] = (data || [])
        .filter((userTenant: any) => !userTenant.tenants?.deactivated_at)
        .map((
          userTenant: any,
        ) => ({
          ...userTenant.tenants,
          userRole: userTenant.role as "owner" | "admin" | "manager" | "member" | "viewer" | "staff",
        } as Tenant));

      setTenants(transformedTenants);
      setLoadError(null);

      // Preload critical data for the first tenant
      if (transformedTenants.length > 0) {
        const firstTenant = transformedTenants[0];
        preloadCriticalData(firstTenant.id);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      // Fallback to direct tenant fetch if user_tenants table doesn't exist yet
      try {
        const { data, error } = await supabase
          .from("tenants")
          .select("*")
          .is("deactivated_at", null)
          .order("created_at", { ascending: false });

        if (error) throw error;

        const transformedTenants: Tenant[] = (data || []).map((tenant: any) => ({
          ...tenant,
        } as Tenant));

        setTenants(transformedTenants);
        setLoadError(null);
      } catch (fallbackError) {
        console.error("Error fetching tenants (fallback):", fallbackError);
        // Beide Wege fehlgeschlagen -- das ist eine Stoerung, keine leere Liste.
        setLoadError(
          fallbackError instanceof Error ? fallbackError : new Error(String(fallbackError))
        );
      }
    } finally {
      setIsLoading(false);
      setHasCheckedTenants(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchTenants();
    } else {
      setTenants([]);
      setIsLoading(false);
      setHasCheckedTenants(false);
    }
  }, [user, fetchTenants]);

  // Preload critical data for faster tenant switching
  const preloadCriticalData = async (tenantId: string) => {
    try {
      // Preload services and team members in parallel
      const [servicesPromise, teamMembersPromise] = [
        supabase
          .from("appointment_types")
          .select("*")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false }),
        supabase
          .from("team_members")
          .select("*")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false }),
      ];

      // Execute in parallel for faster loading
      await Promise.all([servicesPromise, teamMembersPromise]);
    } catch (error) {
      console.error("Error preloading tenant data:", error);
    }
  };


  // Helper function to check if user has permission to modify tenant
  const checkTenantPermission = async (tenantId: string, requiredRole?: string) => {
    if (!user) throw new Error("User not authenticated");

    const { data, error } = await supabase
      .from("user_tenants")
      .select("role")
      .eq("user_id", user.id)
      .eq("tenant_id", tenantId)
      .eq("is_active", true)
      .single();

    if (error || !data) {
      throw new Error("User does not have permission to access this tenant");
    }

    // Check if user has required role (if specified)
    if (requiredRole) {
      const roleHierarchy = ["viewer", "member", "manager", "admin", "owner"];
      const userRoleIndex = roleHierarchy.indexOf(data.role);
      const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);
      
      if (userRoleIndex < requiredRoleIndex) {
        throw new Error(`User role '${data.role}' does not have sufficient permissions. Required: '${requiredRole}' or higher.`);
      }
    }

    return data.role;
  };

  const updateTenantSettings = async (
    tenantId: string,
    settings: TenantSettings,
  ) => {
    if (!user) throw new Error("User not authenticated");

    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ settings: settings as any })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant settings:", error);
      throw error;
    }
  };

  const updateTenantLanguage = async (tenantId: string, language: string) => {
    if (!user) throw new Error("User not authenticated");

    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ language })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant language:", error);
      throw error;
    }
  };

  const updateTenantName = async (tenantId: string, name: string) => {
    if (!user) throw new Error("User not authenticated");

    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ name })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant name:", error);
      throw error;
    }
  };

  const updateTenantLogo = async (tenantId: string, logoUrl: string) => {
    if (!user) throw new Error("User not authenticated");

    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ logo_url: logoUrl })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant logo:", error);
      throw error;
    }
  };

  const updateTenantPrimaryColor = async (tenantId: string, primaryColor: string) => {
    if (!user) throw new Error("User not authenticated");
    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ primary_color: primaryColor })
        .eq("id", tenantId)
        .select()
        .single();
      if (error) throw error;
      const transformedTenant: Tenant = { ...data } as Tenant;
      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );
      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant primary color:", error);
      throw error;
    }
  };

  const updateTenantAddress = async (tenantId: string, address: string, city?: string) => {
    if (!user) throw new Error("User not authenticated");
    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ address, city })
        .eq("id", tenantId)
        .select()
        .single();
      if (error) throw error;
      const transformedTenant: Tenant = { ...data } as Tenant;
      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );
      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant address:", error);
      throw error;
    }
  };

  const updateTenantEmail = async (tenantId: string, email: string) => {
    if (!user) throw new Error("User not authenticated");
    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ email })
        .eq("id", tenantId)
        .select()
        .single();
      if (error) throw error;
      const transformedTenant: Tenant = { ...data } as Tenant;
      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );
      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant email:", error);
      throw error;
    }
  };

  const updateTenantGoogleProfile = async (
    tenantId: string,
    googleData: {
      google_place_id: string;
      google_cid?: string;
      google_maps_review_url?: string;
      location_coordinate?: string;
    }
  ) => {
    if (!user) throw new Error("User not authenticated");
    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({
          google_place_id: googleData.google_place_id,
          google_cid: googleData.google_cid,
          google_maps_review_url: googleData.google_maps_review_url,
          location_coordinate: googleData.location_coordinate,
        })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant Google profile:", error);
      throw error;
    }
  };

  const updateTenantGBPConnection = async (
    tenantId: string,
    gbpData: {
      gbp_access_token: string | null;
      gbp_refresh_token: string | null;
      gbp_token_expires_at: string | null;
      gbp_account_id: string | null;
      gbp_location_id: string | null;
      gbp_location_name: string | null;
      gbp_connected_at: string | null;
    }
  ) => {
    if (!user) throw new Error("User not authenticated");
    try {
      // Check if user has permission to modify tenant (requires manager or higher)
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({
          gbp_access_token: gbpData.gbp_access_token,
          gbp_refresh_token: gbpData.gbp_refresh_token,
          gbp_token_expires_at: gbpData.gbp_token_expires_at,
          gbp_account_id: gbpData.gbp_account_id,
          gbp_location_id: gbpData.gbp_location_id,
          gbp_location_name: gbpData.gbp_location_name,
          gbp_connected_at: gbpData.gbp_connected_at,
        })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      // Update the tenant in local state
      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating tenant GBP connection:", error);
      throw error;
    }
  };

  const updateBookingNotice = async (tenantId: string, enabled: boolean, text: string | null) => {
    if (!user) throw new Error("User not authenticated");

    try {
      await checkTenantPermission(tenantId, "manager");

      const { data, error } = await supabase
        .from("tenants")
        .update({ booking_notice_enabled: enabled, booking_notice_text: text })
        .eq("id", tenantId)
        .select()
        .single();

      if (error) throw error;

      const transformedTenant: Tenant = { ...data } as Tenant;

      setTenants(
        tenants.map((tenant) =>
          tenant.id === tenantId ? transformedTenant : tenant
        ),
      );

      return { data: transformedTenant, error: null as any };
    } catch (error) {
      console.error("Error updating booking notice:", error);
      throw error;
    }
  };

  return {
    tenants,
    isLoading,
    hasCheckedTenants,
    loadError,
    updateTenantSettings,
    updateTenantLanguage,
    updateTenantName,
    updateTenantLogo,
    updateTenantPrimaryColor,
    updateTenantAddress,
    updateTenantEmail,
    updateTenantGoogleProfile,
    updateTenantGBPConnection,
    updateBookingNotice,
    refetch: fetchTenants,
  };
};
